import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { QestlabListDropdown } from 'Components/List';
import ListType from 'Components/List/QestlabListDropdown/ListType';
import { WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { combine } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { pipe } from 'rxjs';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, PageSection } from '../../../../Layout';
import { FdMeasurementApplication } from '../../types';

const Section: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const gaugeCalibrationImportMismatch = useSelector(
    createWorksheetSelector!('Preparation.NuclearDensityGaugeCalibratedImportMismatch')
  );
  const gaugeAdditionalDataImportMismatch = useSelector(
    createWorksheetSelector!('Preparation.NuclearDensityGaugePropertiesImportMismatch')
  );
  const gaugeId = useSelector(createWorksheetSelector!('Preparation.NuclearDensityGauge.Id')) as Guid;
  const gaugeSerialNumber = useSelector(createWorksheetSelector!('Preparation.NuclearDensityGaugeSerialNumber'));
  const gaugeMakeAndModel = useSelector(createWorksheetSelector!('Preparation.NuclearDensityGaugeMakeAndModel'));
  const gaugeModelNumber = useSelector(createWorksheetSelector!('Preparation.NuclearDensityGaugeModelNumber'));

  const applyCorrections = useSelector(
    pipe(createWorksheetSelector!('Preparation.ApplyCorrections'), valueOrDefault(false))
  );

  const isCorrectionFactorEntered = useSelector(
    combine(
      [
        'Preparation.BulkDensityCorrectionSlope',
        'Preparation.BulkDensityCorrectionIntercept',
        'Preparation.MoistureDensityCorrectionSlope',
        'Preparation.MoistureDensityCorrectionIntercept',
      ].map((f) => createWorksheetSelector!(f)),
      (...fields) => fields.some(hasValue)
    )
  );

  const confirmMeasurementApplicationChange = useSelector(
    combine(
      [
        'Preparation.MeasurementApplication',
        'Retest.IsRetest',
        'IsParticleDensityAssumed',
        'Retest.OriginalTest.ImportedTest',
        'ParticleDensityAssumed',
        'ReferenceDensity.ImportedTest',
      ].map((f) => createWorksheetSelector!(f)),
      (measurementApplication, isRetest, isParticleDensityAssumed, ...fieldsClearedForNonComplianceTests) =>
        measurementApplication === FdMeasurementApplication.ComplianceTest &&
        (isRetest ||
          isParticleDensityAssumed === false || // The default state of this field is always true. This won't detect the field being changed and changed back but we're more concerned about losing ParticleDensityAssumed anyway.
          fieldsClearedForNonComplianceTests.some(hasValue))
    )
  );

  const testNumber = useSelector(createWorksheetSelector!('Preparation.TestNumber'));
  return (
    <PageSection heading="Preparation">
      <ListFieldSection>
        <Field input={<TextOutput />} name="Preparation.TestNumber" visible={hasValue(testNumber)} />
        <Field input={<QestlabListDropdown />} label="Weather" name="Preparation.Weather" listType={ListType.Weather} />
      </ListFieldSection>

      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="Preparation.MeasurementApplication"
          options={[
            { label: 'Ground investigation comparative tests', value: FdMeasurementApplication.ComparativeTests },
            { label: 'Ground investigation absolute tests', value: FdMeasurementApplication.AbsoluteTests },
            { label: 'Compliance test for compacted material', value: FdMeasurementApplication.ComplianceTest },
          ]}
          confirm={confirmMeasurementApplicationChange}
          confirmText={`This action will delete data entered in the Retest and Laboratory Density Results sections. Are you sure you want to continue?`}
        />
        <FieldSet>
          <Field
            input={<WorksheetEquipmentDropdown />}
            equipmentType="nuclear-density-gauge"
            name="Preparation.NuclearDensityGauge"
            importedCalibration={{
              Mismatch: gaugeCalibrationImportMismatch,
              ModelNumber: gaugeModelNumber,
            }}
            importedAdditionalData={{
              Mismatch: gaugeAdditionalDataImportMismatch,
              SerialNumber: gaugeSerialNumber,
              MakeAndModel: gaugeMakeAndModel,
            }}
            importModel={{ Id: gaugeId }}
          />
          <Field input={<TextOutput />} label="Serial Number" name="Preparation.NuclearDensityGaugeSerialNumber" />
          <Field input={<TextOutput />} label="Make/Model" name="Preparation.NuclearDensityGaugeMakeAndModel" />
        </FieldSet>
      </ListFieldSection>

      <ListFieldSection>
        <Field
          input={<Checkbox />}
          name="Preparation.ApplyCorrections"
          confirm={applyCorrections && isCorrectionFactorEntered}
          confirmText={'This action will delete the entered correction factors. Are you sure you want to continue?'}
        />
      </ListFieldSection>

      {applyCorrections && (
        <>
          <ListFieldSection heading="Bulk Density Correction Factor">
            <Field input={<NumericInput />} label="Slope (b)" name="Preparation.BulkDensityCorrectionSlope" />{' '}
            {/*TODO: B is capitalize ? noRecase={true}*/}
            <Field input={<NumericInput />} label="Intercept" name="Preparation.BulkDensityCorrectionIntercept" />
          </ListFieldSection>
          <ListFieldSection heading="Moisture Density Correction Factor">
            <Field input={<NumericInput />} label="Slope (b)" name="Preparation.MoistureDensityCorrectionSlope" />{' '}
            {/*TODO: B is capitalize ? noRecase={true}  */}
            <Field input={<NumericInput />} label="Intercept" name="Preparation.MoistureDensityCorrectionIntercept" />
          </ListFieldSection>
        </>
      )}
    </PageSection>
  );
};

const BsPreparationSection = withInjectedProps(Section);

export { BsPreparationSection };
export default BsPreparationSection;
